import * as React from "react";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Field } from "@/components/ui/field";
import { Button } from "@/components/ui/button";
import {
  resendSignupVerification,
  sendSignupVerification,
} from "@/lib/verification.functions";
import { ResendLinkForm } from "./-verify-email-shared";
import { buildHandoff, clearHandoff, readHandoff, type FinderAnswers } from "@/lib/finder-handoff";
import { readAffiliateSlug, readGrCookieId } from "@/lib/partner-link";
import { recordPartnerReferral } from "@/lib/partner-attribution.functions";

type Mode = "signin" | "signup" | "reset" | "update-password";

type AuthSearch = {
  mode: Mode;
  redirect?: string;
  notice?: "verified";
  ranking_category?: string;
  ranking_stage?: string;
  /** Homepage Top 10 finder selections. Step B persists these properly. */
  raise_type?: string;
  company_stage?: string;
  company_sector?: string;
  re_capital_type?: string;
  property_type?: string;
  fund_profile?: string;
  fund_strategy?: string;
  loan_type?: string;
  /** Opaque token for the finder selections held in local storage. */
  finder?: string;
};

/** Optional finder parameters carried through from the homepage. */
const FINDER_PARAMS = [
  "raise_type",
  "company_stage",
  "company_sector",
  "re_capital_type",
  "property_type",
  "fund_profile",
  "fund_strategy",
  "loan_type",
  "finder",
] as const;

/** Trims and length-caps an optional ranking attribution param. */
function cleanRankingParam(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim().slice(0, 120);
  return trimmed.length > 0 ? trimmed : undefined;
}

const VALID_MODES: ReadonlyArray<Mode> = ["signin", "signup", "reset", "update-password"];

export const Route = createFileRoute("/auth")({
  validateSearch: (search: Record<string, unknown>): AuthSearch => {
    const rawMode = typeof search.mode === "string" ? search.mode : "signin";
    const mode: Mode = (VALID_MODES as readonly string[]).includes(rawMode)
      ? (rawMode as Mode)
      : "signin";
    const redirectTo =
      typeof search.redirect === "string" && search.redirect.startsWith("/")
        ? search.redirect
        : undefined;
    const notice =
      search.notice === "verified" ? ("verified" as const) : undefined;
    const finder: Record<string, string> = {};
    for (const key of FINDER_PARAMS) {
      const value = cleanRankingParam(search[key]);
      if (value) finder[key] = value;
    }
    return {
      mode,
      redirect: redirectTo,
      notice,
      ranking_category: cleanRankingParam(search.ranking_category),
      ranking_stage: cleanRankingParam(search.ranking_stage),
      ...finder,
    };
  },
  beforeLoad: async ({ search }) => {
    if (search.mode === "update-password") return;
    if (typeof window === "undefined") return;
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      throw redirect({ to: search.redirect ?? "/dashboard" });
    }
  },
  head: () => ({
    meta: [
      { title: "Sign in: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const { mode } = Route.useSearch();

  return (
    <main className="min-h-screen bg-brand-paper-2 px-4 py-12 sm:py-16">
      <article className="mx-auto max-w-[420px] overflow-hidden rounded-2xl border border-brand-line bg-brand-paper shadow-[0_1px_0_var(--brand-line),0_30px_60px_-30px_oklch(0.264_0.068_255.3/0.18)]">
        <div className="px-[28px] pt-10 pb-9">
          <div className="mb-7 flex flex-col items-center text-center">
            <Link to="/" className="text-brand-ink no-underline">
              <span className="font-sans text-[15px] font-semibold tracking-tight">
                Capital<span className="text-brand-accent">Dart</span>
              </span>
            </Link>
          </div>
          {mode === "signin" && <SignInView />}
          {mode === "signup" && <SignUpView />}
          {mode === "reset" && <ResetView />}
          {mode === "update-password" && <UpdatePasswordView />}
        </div>
      </article>
    </main>
  );
}

function ErrorBanner({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-[14px] text-destructive">
      {message}
    </div>
  );
}

function AuthHeading({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="mb-5 text-center">
      <h1 className="font-serif text-[28px] font-normal leading-tight tracking-tight text-brand-ink">
        {title}
      </h1>
      <p className="mt-1.5 text-[13.5px] text-brand-muted">{sub}</p>
    </div>
  );
}

function AltLink({
  prompt,
  linkText,
  to,
  mode,
  redirect: redirectTo,
}: {
  prompt: string;
  linkText: string;
  to: "/auth";
  mode: Mode;
  redirect?: string;
}) {
  return (
    <p className="mt-5 text-center text-[14px] text-brand-muted">
      {prompt}{" "}
      <Link
        to={to}
        search={{ mode, redirect: redirectTo }}
        className="font-medium text-brand-ink no-underline hover:text-brand-accent"
      >
        {linkText}
      </Link>
    </p>
  );
}

export const CONSENT_PATH = "/.lovable/oauth/consent";

/** True when the redirect param points at the OAuth consent screen. */
export function isConsentRedirect(value: string | undefined): boolean {
  return typeof value === "string" && value.startsWith(CONSENT_PATH);
}

function ConnectBanner() {
  return (
    <p className="mb-4 rounded-md border border-brand-line bg-brand-paper-2 px-3 py-2 text-center text-[14px] font-medium text-brand-ink">
      Connect your assistant to CapitalDart
    </p>
  );
}


function SignInView() {
  const navigate = useNavigate();
  const { redirect: redirectTo, notice } = Route.useSearch();
  const resendByEmail = useServerFn(resendSignupVerification);
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [unconfirmed, setUnconfirmed] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setUnconfirmed(false);
    setLoading(true);
    const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (authError) {
      if (/email not confirmed/i.test(authError.message || "")) {
        setUnconfirmed(true);
        setError(
          "Your email address is not confirmed yet. Send yourself a new confirmation link below.",
        );
      } else {
        setError(authError.message);
      }
      return;
    }
    navigate({ to: redirectTo ?? "/dashboard" });
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      {isConsentRedirect(redirectTo) && <ConnectBanner />}
      <AuthHeading title="Welcome back." sub="Sign in to your CapitalDart account." />

      {notice === "verified" && (
        <div className="mb-4 rounded-md border border-brand-line bg-[oklch(0.96_0.05_155.7)] px-3 py-2 text-[13px] text-[oklch(0.35_0.087_155.7)]">
          Email confirmed. Log in to continue.
        </div>
      )}
      <div className="border-t border-brand-line">
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Field
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <ErrorBanner message={error} />
      {unconfirmed && (
        <div className="mt-4 rounded-md border border-brand-line bg-brand-paper-2 p-3">
          <ResendLinkForm send={resendByEmail} />
        </div>
      )}
      <div className="mt-5 flex flex-col gap-3">
        <Button type="submit" disabled={loading} className="w-full">
          {loading ? "Signing in…" : "Sign in →"}
        </Button>
        <Link
          to="/auth"
          search={{ mode: "reset" as const }}
          className="text-center text-[14px] text-brand-muted no-underline hover:text-brand-ink"
        >
          Forgot password?
        </Link>
      </div>
      <AltLink
        prompt="Don’t have an account?"
        linkText="Create one"
        to="/auth"
        mode="signup"
        redirect={redirectTo}
      />
    </form>
  );
}

function SignUpView() {
  const finderParams = Route.useSearch();
  const {
    redirect: redirectTo,
    ranking_category: rankingCategory,
    ranking_stage: rankingStage,
    finder: finderToken,
    raise_type: finderRaiseType,
  } = finderParams;
  const sendVerification = useServerFn(sendSignupVerification);

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [agreed, setAgreed] = React.useState(false);
  const [agreeError, setAgreeError] = React.useState<string | null>(null);
  const [sentEmail, setSentEmail] = React.useState<string | null>(null);
  const [sendWarning, setSendWarning] = React.useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!agreed) {
      setAgreeError(
        "You must accept the Terms of Service and Privacy Policy to create an account.",
      );
      return;
    }
    setAgreeError(null);
    setLoading(true);
    // Screen: dotted local-part abuse. Local parts with 3+ dots are blocked
    // regardless of domain (mainstream provider allowlist does NOT bypass).
    // Runs BEFORE the disposable-domain RPC so gmail.com etc. are still checked.
    {
      const local = (email.split("@")[0] || "");
      const domain = (email.split("@")[1] || "").toLowerCase();
      const dotCount = (local.match(/\./g) || []).length;
      if (dotCount > 2) {
        setLoading(false);
        setError("Please sign up with a work or personal email address.");
        try {
          void supabase
            .from("blocked_signup_attempts")
            .insert({
              email,
              domain,
              matched_domain: domain,
              source: "signup_ui_precheck_local_part_dots",
            });
        } catch {
          // never surface log errors to the user
        }
        return;
      }
    }
    // Pre-check disposable email domains via RPC. The database also has a
    // BEFORE INSERT backstop, but this gives a friendly message without a
    // network round trip through Supabase auth error mapping.
    try {
      const { data: isDisposable, error: rpcError } = await supabase.rpc(
        "is_disposable_email",
        { p_email: email },
      );
      if (!rpcError && isDisposable === true) {
        setLoading(false);
        setError("Please sign up with a work or personal email address.");
        // Best-effort audit log: fire-and-forget, must not block the
        // rejection or throw into the UI. Direct-API signups (bypassing
        // this UI) are still blocked by the auth.users trigger but are
        // NOT captured here, because Postgres rolls back the trigger's
        // log INSERT alongside the RAISE. dblink is not installed and
        // pg_net queue writes are transactional, so autonomous logging
        // from the trigger is not available in this project today.
        try {
          const domain = (email.split("@")[1] || "").toLowerCase();
          void supabase
            .from("blocked_signup_attempts")
            .insert({
              email,
              domain,
              matched_domain: domain,
              source: "signup_ui_precheck",
            });
        } catch {
          // never surface log errors to the user
        }
        return;
      }
    } catch {
      // If the pre-check fails, fall through; the DB trigger still enforces.
    }
    // Ranking-page attribution: persisted as Supabase user metadata at
    // account creation, so it survives the email confirmation round trip
    // without any redirect plumbing. Nothing consumes it yet.
    const rankingMetadata =
      rankingCategory || rankingStage
        ? {
            ...(rankingCategory ? { ranking_category: rankingCategory } : {}),
            ...(rankingStage ? { ranking_stage: rankingStage } : {}),
          }
        : null;
    // Top 10 finder selections: read back from local storage with the opaque
    // token in the URL, falling back to the plain parameters when storage was
    // cleared. Written as account metadata so verification on another device
    // still lands the prefill.
    const storedHandoff = readHandoff(finderToken);
    const handoff =
      storedHandoff ??
      (finderRaiseType
        ? buildHandoff(finderRaiseType, {
            company_stage: finderParams.company_stage,
            company_sector: finderParams.company_sector,
            re_capital_type: finderParams.re_capital_type,
            property_type: finderParams.property_type,
            fund_profile: finderParams.fund_profile,
            fund_strategy: finderParams.fund_strategy,
            loan_type: finderParams.loan_type,
          } as FinderAnswers)
        : null);
    const signUpMetadata =
      rankingMetadata || handoff
        ? {
            ...(rankingMetadata ?? {}),
            ...(handoff ? { finder_handoff: JSON.stringify(handoff) } : {}),
          }
        : null;
    const { data: signUpData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      ...(signUpMetadata ? { options: { data: signUpMetadata } } : {}),
    });
    if (authError) {
      setLoading(false);
      const msg = authError.message || "";
      if (/work or personal email address/i.test(msg) || /disposable/i.test(msg)) {
        setError("Please sign up with a work or personal email address.");
      } else {
        setError(msg);
      }
      return;
    }
    // Partner attribution: fire only on successful new signup.
    // 1) the documented browser snippet, which credits the signup to the
    //    partner whose link brought the visitor in, and
    // 2) our own copy of the link slug and tracking cookie, so the Stripe
    //    webhook can name the partner and apply the self-referral guard.
    try {
      if (typeof window !== "undefined" && typeof (window as unknown as { gr?: (...args: unknown[]) => void }).gr === "function") {
        (window as unknown as { gr: (...args: unknown[]) => void }).gr("track", "conversion", { email, uid: signUpData.user?.id });
      }
    } catch {
      // never let partner tracking break signup
    }
    try {
      if (typeof window !== "undefined") {
        const affiliateSlug = readAffiliateSlug(window.location.search);
        const grId = readGrCookieId(document.cookie || "");
        if (affiliateSlug || grId) {
          void recordPartnerReferral({
            data: {
              userId: signUpData.user?.id ?? null,
              email,
              affiliateSlug,
              grId,
              landingUrl: window.location.href.slice(0, 500),
            },
          });
        }
      }
    } catch {
      // never let partner tracking break signup
    }
    // The selections now live on the account, so the browser copy can go.
    clearHandoff(finderToken);
    // No session is issued now: the account is created but unconfirmed.
    // Send the confirmation email directly from the server request path
    // (bypassing the pg_net trigger) and await Resend before showing the
    // "Check your email" screen so the user only sees success once the
    // message is actually handed off.
    let warning: string | null = null;
    try {
      const result = await sendVerification({
        data: {
          email,
          ...(isConsentRedirect(redirectTo) ? { redirectTo } : {}),
        },
      });

      if (!result.ok) warning = result.message;
    } catch {
      warning =
        "We are having trouble sending the confirmation email. Try Resend in a moment.";
    }
    setLoading(false);
    setSendWarning(warning);
    setSentEmail(email);
  }

  if (sentEmail) {
    return <CheckYourEmailView email={sentEmail} warning={sendWarning} />;
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      {isConsentRedirect(redirectTo) && <ConnectBanner />}
      <AuthHeading title="Create your account." sub="Start your first deal in under a minute." />

      <div className="border-t border-brand-line">
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Field
          label="Password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          hint="At least 8 characters."
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <ErrorBanner message={error} />
      <label className="mt-5 flex items-start gap-2.5 text-[13.5px] leading-[1.5] text-brand-muted">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => {
            setAgreed(e.target.checked);
            if (e.target.checked) setAgreeError(null);
          }}
          className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-brand-ink"
          required
        />
        <span>
          I have read and agree to the{" "}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-ink underline decoration-brand-line underline-offset-4 hover:text-brand-accent"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-ink underline decoration-brand-line underline-offset-4 hover:text-brand-accent"
          >
            Privacy Policy
          </a>
          .
        </span>
      </label>
      <ErrorBanner message={agreeError} />
      <Button type="submit" disabled={loading || !agreed} className="mt-4 w-full">
        {loading ? "Creating account…" : "Create account →"}
      </Button>
      {isConsentRedirect(redirectTo) && (
        <p className="mt-2 text-center text-[13px] text-brand-muted">
          Free trial. No credit card.
        </p>
      )}
      <AltLink
        prompt="Already have an account?"
        linkText="Sign in"
        to="/auth"
        mode="signin"
        redirect={redirectTo}
      />

    </form>
  );
}

function CheckYourEmailView({
  email,
  warning,
}: {
  email: string;
  warning?: string | null;
}) {
  const resend = useServerFn(resendSignupVerification);
  const [showRescue, setShowRescue] = React.useState(false);
  const mutation = useMutation({
    mutationFn: () => resend({ data: { email } }),
  });

  React.useEffect(() => {
    const t = window.setTimeout(() => setShowRescue(true), 3 * 60 * 1000);
    return () => window.clearTimeout(t);
  }, []);

  const supportSubject = encodeURIComponent(
    "Confirmation link not arriving",
  );
  const supportBody = encodeURIComponent(
    `Hi CapitalDart team,\n\nI signed up with ${email} but the confirmation link has not arrived. Please help.\n\nThanks.`,
  );
  const wrongAddressBody = encodeURIComponent(
    `Hi CapitalDart team,\n\nI signed up with ${email} but that address is wrong. Please help me correct it.\n\nThanks.`,
  );

  return (
    <div>
      <AuthHeading
        title="Check your email."
        sub="One click and you are in."
      />
      <p className="text-[14px] leading-relaxed text-brand-ink">
        We sent a sign-in link to{" "}
        <strong className="text-brand-ink">{email}</strong>. Click it from
        any device to finish.
      </p>
      {warning && (
        <p className="mt-3 rounded-md border border-brand-line bg-brand-paper-2 px-3 py-2 text-[13px] leading-relaxed text-brand-ink">
          {warning}
        </p>
      )}
      <div className="mt-5 flex flex-col gap-2">
        <Button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          variant="outline"
          className="w-full"
        >
          {mutation.isPending ? "Sending…" : "Resend link"}
        </Button>
        {mutation.data && mutation.data.ok && (
          <p className="text-[13px] text-brand-muted">
            A new link is on its way. Check your inbox.
          </p>
        )}
        {mutation.data && !mutation.data.ok && (
          <p className="text-[13px] text-destructive">{mutation.data.message}</p>
        )}
      </div>
      <p className="mt-5 text-[13px] leading-relaxed text-brand-muted">
        Typed the wrong address?{" "}
        <a
          href={`mailto:support@capitaldart.com?subject=${encodeURIComponent(
            "Fix my signup email address",
          )}&body=${wrongAddressBody}`}
          className="text-brand-ink underline decoration-brand-line underline-offset-4 hover:text-brand-accent"
        >
          Email support
        </a>{" "}
        and we will move your account.
      </p>
      {showRescue && (
        <p className="mt-4 rounded-md border border-brand-line bg-brand-paper-2 p-3 text-[13px] leading-relaxed text-brand-ink">
          Still nothing after a few minutes? Corporate mail filters
          occasionally hold automated messages.{" "}
          <a
            href={`mailto:support@capitaldart.com?subject=${supportSubject}&body=${supportBody}`}
            className="text-brand-ink underline decoration-brand-line underline-offset-4 hover:text-brand-accent"
          >
            Send us an email
          </a>{" "}
          and we will confirm your account by hand.
        </p>
      )}
      <AltLink
        prompt="Already confirmed?"
        linkText="Sign in"
        to="/auth"
        mode="signin"
      />
    </div>
  );
}

function ResetView() {
  const [email, setEmail] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [done, setDone] = React.useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const redirectTo =
      typeof window !== "undefined"
        ? `${window.location.origin}/auth?mode=update-password`
        : undefined;
    const { error: authError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });
    setLoading(false);
    if (authError) {
      setError(authError.message);
      return;
    }
    setDone(true);
  }

  if (done) {
    return (
      <div>
        <AuthHeading title="Check your inbox." sub="Reset link sent." />
        <p className="text-[14px] leading-relaxed text-brand-muted">
          If an account exists for <strong className="text-brand-ink">{email}</strong>, we sent a
          password reset link. Click it to set a new password.
        </p>
        <Button asChild variant="outline" className="mt-5 w-full">
          <Link to="/auth" search={{ mode: "signin" as const }}>Back to sign in</Link>
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <AuthHeading title="Reset your password." sub="Enter your email and we'll send you a reset link." />
      <div className="border-t border-brand-line">
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <ErrorBanner message={error} />
      <div className="mt-5 flex flex-col gap-3">
        <Button type="submit" disabled={loading} className="w-full">
          {loading ? "Sending…" : "Send reset link"}
        </Button>
        <Link
          to="/auth"
          search={{ mode: "signin" as const }}
          className="text-center text-[14px] text-brand-muted no-underline hover:text-brand-ink"
        >
          Back to sign in
        </Link>
      </div>
    </form>
  );
}

function UpdatePasswordView() {
  const navigate = useNavigate();
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    // Supabase parses the recovery hash and fires a PASSWORD_RECOVERY event.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
        setReady(true);
      }
    });
    // Also accept an already-active session (user arrived signed-in).
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error: authError } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (authError) {
      setError(authError.message);
      return;
    }
    navigate({ to: "/dashboard" });
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <AuthHeading title="Set a new password." sub="Choose a strong password to finish resetting." />
      {!ready && (
        <p className="mt-3 text-[14px] text-brand-muted">
          Verifying your reset link…
        </p>
      )}
      <div className="border-t border-brand-line">
        <Field
          label="New password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          hint="At least 8 characters."
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={!ready}
        />
      </div>
      <ErrorBanner message={error} />
      <Button type="submit" disabled={loading || !ready} className="mt-5 w-full">
        {loading ? "Updating…" : "Update password"}
      </Button>
    </form>
  );
}